import Button from '../../../components/base/Button';

export default function FeaturesSection() {
  const features = [
    {
      icon: "ri-brain-line",
      title: "Predictive Disruption Forecasting",
      description: "Converting scattered signals into foresight you can trust, so you stay ahead of disruption, not stuck in it.",
      gradientFrom: '#0A0A6B',
      gradientTo: '#2C2CE8'
    },
    {
      icon: "ri-smartphone-line",
      title: "Traveler-First Insights",
      description: "Proactive, precise, and to the point. Flyte delivers only the updates that keep your plans on track.",
      gradientFrom: '#5375EE',
      gradientTo: '#2C2CE8'
    },
    {
      icon: "ri-flight-takeoff-line",
      title: "Personalized Alternatives",
      description: "Adapting to what matters most: your price point, preferred routes, and chosen airlines - surfacing better options instantly.",
      gradientFrom: '#2C2CE8',
      gradientTo: '#5375EE'
    }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="features" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Built For Travelers. By Travelers.
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our technology turns complex travel data into simple, meaningful insights that keep your trip on track.
          </p>
        </div>
        
        {/* Three features in boxed layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
          {features.map((feature, index) => (
            <div key={index} className="group">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 h-full transition-all duration-300 group-hover:shadow-xl group-hover:-translate-y-2 flex flex-col items-center text-center">
                <div 
                  className="w-20 h-20 flex items-center justify-center rounded-full mb-6"
                  style={{ 
                    background: `linear-gradient(135deg, ${feature.gradientFrom} 0%, ${feature.gradientTo} 100%)` 
                  }}
                >
                  <i className={`${feature.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Clean Disruption Flow */}
        <div className="mb-24">
          <div className="bg-white rounded-3xl p-8 md:p-14 shadow-xl text-white overflow-hidden relative border border-gray-100">
            
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-72 h-72 bg-gray-50 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-56 h-56 bg-gray-50 rounded-full translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

            <div className="text-center mb-12 relative z-10">
              <span className="inline-block bg-[#2C2CE8]/10 text-[#2C2CE8] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4">The Ripple Effect</span>
              <h3 className="text-3xl md:text-5xl font-bold mb-4 text-gray-900">
                The Disruption <span className="text-[#2C2CE8]">Domino Effect</span>
              </h3>
              <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
                A single disruption doesn't stop at the gate — it ignites a ripple felt across every layer of travel.
              </p>
            </div>

            {/* Flow Layout */}
            <div className="relative z-10 max-w-5xl mx-auto">

              {/* Step 1 — Initial Disruption */}
              <div className="flex flex-col items-center mb-4">
                <div className="w-full max-w-sm bg-[#2C2CE8]/10 border border-[#2C2CE8]/20 backdrop-blur-sm rounded-2xl p-6 text-center shadow-lg">
                  <div className="w-12 h-12 mx-auto mb-3 bg-[#2C2CE8]/20 rounded-full flex items-center justify-center">
                    <i className="ri-alert-fill text-[#2C2CE8] text-xl"></i>
                  </div>
                  <div className="text-xs font-bold tracking-widest uppercase text-gray-400 mb-1">Trigger</div>
                  <h4 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">Initial Disruption</h4>
                  <p className="text-gray-500 text-sm">Last-minute cancellation leaves passengers stranded</p>
                </div>
                {/* Connector */}
                <div className="flex flex-col items-center my-1">
                  <div className="w-0.5 h-6 bg-gray-300"></div>
                  <i className="ri-arrow-down-s-line text-gray-400 text-xl -mt-1"></i>
                </div>
              </div>

              {/* Step 2 — Immediate Effects */}
              <div className="mb-4">
                <div className="text-center mb-4">
                  <span className="text-xs font-bold tracking-widest uppercase text-gray-400">Immediate Effects</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                  {[
                    { icon: "ri-group-line", title: "Passenger Chaos", desc: "Crowds compete for limited rebooking options" },
                    { icon: "ri-phone-line", title: "System Overload", desc: "Customer service lines jam with frustrated travelers" },
                  ].map((item, i) => (
                    <div key={i} className="bg-gray-50 border border-gray-200 rounded-xl p-5 text-center shadow-md">
                      <div className="w-10 h-10 mx-auto mb-3 bg-[#2C2CE8]/15 rounded-full flex items-center justify-center">
                        <i className={`${item.icon} text-[#2C2CE8] text-lg`}></i>
                      </div>
                      <h6 className="text-base font-bold text-gray-900 mb-1">{item.title}</h6>
                      <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col items-center my-1">
                  <div className="w-0.5 h-6 bg-gray-300"></div>
                  <i className="ri-arrow-down-s-line text-gray-400 text-xl -mt-1"></i>
                </div>
              </div>

              {/* Step 3 — Secondary Effects */}
              <div className="mb-4">
                <div className="text-center mb-4">
                  <span className="text-xs font-bold tracking-widest uppercase text-gray-400">Secondary Effects</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { icon: "ri-calendar-close-line", title: "Missed Connections", desc: "Connecting flights missed, plans disrupted" },
                    { icon: "ri-briefcase-line", title: "Business Impact", desc: "Meetings canceled, deals delayed" },
                    { icon: "ri-heart-line", title: "Missed Commitments", desc: "Family events, vacations ruined" },
                  ].map((item, i) => (
                    <div key={i} className="bg-gray-50 border border-gray-200 rounded-xl p-5 text-center shadow-md">
                      <div className="w-10 h-10 mx-auto mb-3 bg-[#2C2CE8]/15 rounded-full flex items-center justify-center">
                        <i className={`${item.icon} text-[#2C2CE8] text-lg`}></i>
                      </div>
                      <h6 className="text-base font-bold text-gray-900 mb-1">{item.title}</h6>
                      <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col items-center my-1">
                  <div className="w-0.5 h-6 bg-gray-300"></div>
                  <i className="ri-arrow-down-s-line text-gray-400 text-xl -mt-1"></i>
                </div>
              </div>

              {/* Step 4 — Final Impact */}
              <div className="mb-10">
                <div className="text-center mb-4">
                  <span className="text-xs font-bold tracking-widest uppercase text-gray-400">Systemic Fallout</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                  {[
                    { icon: "ri-time-line", title: "178M+ Hours", desc: "Traveler hours wasted annually" },
                    { icon: "ri-global-line", title: "$30B+ Impact", desc: "Annual costs across the U.S. travel ecosystem" },
                  ].map((item, i) => (
                    <div key={i} className="bg-[#2C2CE8]/10 border border-[#2C2CE8]/20 rounded-xl p-6 text-center shadow-lg">
                      <div className="w-10 h-10 mx-auto mb-3 bg-[#2C2CE8]/20 rounded-full flex items-center justify-center">
                        <i className={`${item.icon} text-[#2C2CE8] text-lg`}></i>
                      </div>
                      <h6 className="text-xl font-black text-gray-900 mb-1">{item.title}</h6>
                      <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* Bottom Impact Stats */}
            <div className="relative z-10 mt-4 pt-10 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                <div className="bg-gray-50 rounded-2xl p-6">
                  <div className="text-4xl md:text-5xl font-black mb-2 bg-gradient-to-r from-[#2C2CE8] to-[#5375EE] bg-clip-text text-transparent">85%</div>
                  <p className="text-gray-500 text-sm leading-relaxed">Of disruptions could have been avoided with early warning</p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-6">
                  <div className="text-4xl md:text-5xl font-black mb-2 bg-gradient-to-r from-[#2C2CE8] to-[#5375EE] bg-clip-text text-transparent">$500</div>
                  <p className="text-gray-500 text-sm leading-relaxed">Average out-of-pocket cost per delayed passenger</p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-6">
                  <div className="text-4xl md:text-5xl font-black mb-2 bg-gradient-to-r from-[#2C2CE8] to-[#5375EE] bg-clip-text text-transparent">3 Mos</div>
                  <p className="text-gray-500 text-sm leading-relaxed">Average time for compensation claims via DOT</p>
                </div>
              </div>
            </div>

          </div>
        </div>

        <div className="mt-24">
          <div className="bg-white rounded-2xl p-16 md:p-20 shadow-xl mx-auto">
            <div className="max-w-6xl mx-auto">
              <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12 text-center">
                Every Disruption Has a <span className="text-[#2C2CE8]">Price</span>
              </h3>
              
              {/* Facts Section */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-16 mb-16">
                <div className="bg-gray-50 rounded-2xl p-6 text-center">
                  <div className="text-6xl md:text-7xl font-black mb-6 bg-gradient-to-r from-[#2C2CE8] to-[#5375EE] bg-clip-text text-transparent">
                    1 in 4
                  </div>
                  <p className="text-gray-600 text-lg font-medium leading-relaxed">
                    Passengers experienced a delay or cancellation in the U.S. last year
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-2xl p-6 text-center">
                  <div className="text-6xl md:text-7xl font-black mb-6 bg-gradient-to-r from-[#2C2CE8] to-[#5375EE] bg-clip-text text-transparent">
                    $33B
                  </div>
                  <p className="text-gray-600 text-lg font-medium leading-relaxed">
                    Annual cost of U.S. flight delays &amp; cancellations
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-2xl p-6 text-center">
                  <div className="text-6xl md:text-7xl font-black mb-6 bg-gradient-to-r from-[#2C2CE8] to-[#5375EE] bg-clip-text text-transparent">
                    66K
                  </div>
                  <p className="text-gray-600 text-lg font-medium leading-relaxed">
                    DOT complaints filed by passengers in 2024
                  </p>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                  More than an app—it's a new era of travel. We are changing the way you fly.<br />
                  Turning disruption into decisions <span className="text-[#2C2CE8]">you</span> control.
                </p>
                <div className="flex justify-center">
                  <Button 
                    size="lg"
                    onClick={() => scrollToSection('waitlist')}
                    className="px-12 py-6 text-xl font-semibold whitespace-nowrap cursor-pointer"
                  >
                    Get Early Access
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-4">*2024 data</p>
              </div>
              
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
